import React, { useState, useMemo, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Tabs, Tab, Spinner } from 'react-bootstrap';
import { useTranslation } from '../../i18n/I18nProvider';
import { api } from '../../utils/api';

const AVAILABLE_IMAGES = [
  'a.jpg', 'abbatage.jpg', 'acier.jpg', 'alarme.jpg', 'alarme.jpg.png',
  'amenagement.jpg.jpg', 'appareilmenagers.jpg', 'arbres.jpg', 'arbustesisoles.jpg',
  'arbustres.jpg', 'artisan.jpg', 'assemble.jpg', 'assurance.png', 'autoportant.jpg',
  'baignoire.jpg', 'bain.jpg', 'battant.jpg', 'beton.jpg', 'beton2.webp', 'bois.jpg',
  'bornes.jpg', 'brique.jpg', 'cabinededouche.jpg', 'camera.jpg.jpg', 'cantilever.jpg',
  'capot.jpg', 'carelage.jpg', 'carrelage.jpg', 'ceramique.jpg', 'charniere.jpg',
  'chaudiere.jpg', 'chauffage.jpg', 'chauffe.jpg', 'chauffeur.jpg.png', 'clairiere.jpg',
  'climatisation.jpg', 'cloisons.jpg', 'comptoir.jpg', 'conseil.png', 'controle.jpg.png',
  'coulissant.jpg', 'cuisine.jpg', 'dalles.jpg', 'dcuisine.jpg', 'debaras.jpg',
  'demenagement.jpg.jpg', 'deppanage.jpg.png', 'domotique.jpg', 'douche.jpg',
  'eclairage.jpg', 'elderly.jpg', 'electricien.jpg.png', 'encoupe.jpg', 'enfant.jpg.jpg',
  'entretienvehicule.jpg.png', 'espacesverts.jpg', 'evier.jpg', 'exterieur.jpg',
  'fenetres.jpg', 'feuilles.jpg', 'fleurs.jpg', 'flush.jpg', 'four.jpg', 'free.png',
  'frien.jpg.jpg', 'fuir.jpg', 'garage.jpg', 'garageenhaut.jpg', 'glissement.jpg',
  'grandetaille.jpg', 'haiedense.jpg', 'haielegere.jpg', 'haiemixte.jpg', 'haies.jpg',
  'homestaging.jpg', 'hotte.jpg', 'img.jpg', 'interphone.jpg', 'jardinage.jpg',
  'lavabo.jpg', 'lavevaisselle.jpg', 'logo-dark.png', 'logo-light.png', 'm.jpg',
  'machinealaver.jpg', 'maconnerie.jpg', 'mastic.jpg', 'mcuisine.jpg', 'mdf.jpg',
  'mecanique-aa.png', 'menage.jpg.jpg', 'menage.jpg.png', 'meubledesalledebain.jpg',
  'microondes.jpg', 'mobilier.jpg', 'montage.jpg.jpg', 'msurmesure.jpg', 'murale.jpg',
  'obturateur.jpg', 'ouvertureporte.jpg.png', 'papierpeint.jpg', 'parquet.jpg',
  'peint.jpg', 'peinture.jpg', 'pelouse.jpg', 'petitetaille.jpg', 'pierresnaturelles.jpg',
  'plaque.jpg', 'plaquedecuisson.jpg', 'platrage.jpg', 'platre.jpg', 'pliant.jpg',
  'plinthes.jpg', 'plomberie.jpg', 'pneu.jpg.jpg', 'poele.jpg', 'portail.jpg',
  'porte.jpg', 'porte.jpg.jpg', 'porteexterieur.jpg', 'portegarage.jpg', 'potager.jpg',
  'prestataire.svg', 'prises.jpg', 'publiez.svg', 'pvc.jpg', 'r.jpg.png',
  'recevez.svg', 'refrigerateur.jpg', 'renovation.jpg', 'reparationdeminuiserie.jpg',
  'revetementdesol.jpg', 'rideau.jpg', 'rouleau.jpg', 's.jpg.png', 'salledebain.jpg',
  'satisfer.sg.webp', 'se.jpg.png', 'sechoir.jpg', 'serrurerie.jpg.jpg',
  'servicecuisine.jpg', 'sol.jpg', 'specialise.png', 'store.jpg', 'taillestandard.jpg',
  'tapis.jpg', 'tapis2.jpg', 'terasseenbois.jpg', 'terrase.jpg', 'toilette.jpg',
  'toilettes.jpg', 'tondre.jpg', 'transport.jpg.png', 'trouvez.svg', 'tuile.jpg',
  'tuyaux.jpg', 'verifie.png', 'vestiaire.jpg', 'vidange.jpg.jpg', 'vinyle.jpg',
  'vitre.jpg.png', 'vmc.jpg', 'volet.jpg',
];

function ImagePicker({ show, onHide, onSelect, currentPath }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = useRef(null);

  const filteredImages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return AVAILABLE_IMAGES;
    return AVAILABLE_IMAGES.filter(name => name.toLowerCase().includes(q));
  }, [search]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploadPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      if (res?.success && res.path) {
        onSelect(res.path);
        onHide();
        setUploadPreview(null);
      } else {
        setUploadError(res?.message || t('admin.services.form.uploadFailed'));
      }
    } catch (err) {
      setUploadError(err.message || t('admin.services.form.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && fileInputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
      handleFileSelect({ target: { files: [file] } });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('admin.services.form.imagePicker')}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {currentPath && (
          <div className="mb-3 p-2 border rounded bg-light">
            <small className="text-muted d-block mb-1">{t('admin.services.form.currentImage')}</small>
            <div className="d-flex align-items-center gap-2">
              <img
                src={currentPath}
                alt=""
                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <code>{currentPath}</code>
            </div>
          </div>
        )}

        <Tabs defaultActiveKey="library" className="mb-3">

          {/* ===== Tab: Library ===== */}
          <Tab eventKey="library" title={t('admin.services.form.tabLibrary')}>
            <Form.Control
              type="text"
              placeholder={t('admin.services.form.searchImages')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
            />
            <Row className="g-2">
              {filteredImages.map((name) => {
                const path = `/images/${name}`;
                return (
                  <Col xs={4} md={3} lg={2} key={name}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => { onSelect(path); onHide(); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(path); onHide(); } }}
                      className={`border rounded p-1 text-center ${currentPath === path ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer', transition: 'border-color 0.15s' }}
                    >
                      <img
                        src={path}
                        alt={name}
                        style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 4 }}
                        onError={(e) => { e.target.src = '/images/a.jpg'; }}
                      />
                      <div className="text-truncate small text-muted mt-1" title={name}>
                        {name}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
            {filteredImages.length === 0 && (
              <div className="text-center text-muted py-4">{t('admin.services.form.noImagesFound')}</div>
            )}
          </Tab>

          {/* ===== Tab: Upload ===== */}
          <Tab eventKey="upload" title={t('admin.services.form.tabUpload')}>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #ccc',
                borderRadius: 12,
                padding: '3rem 2rem',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              className="mb-3"
            >
              {uploading ? (
                <div className="d-flex flex-column align-items-center gap-2">
                  <Spinner animation="border" variant="primary" />
                  <span className="text-muted">{t('admin.services.form.uploading')}</span>
                </div>
              ) : uploadPreview ? (
                <div className="d-flex flex-column align-items-center gap-2">
                  <img src={uploadPreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover' }} />
                  <span className="text-muted small">{t('admin.services.form.uploading')}</span>
                </div>
              ) : (
                <div className="text-muted">
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                  <p className="mb-1">{t('admin.services.form.dropOrClick')}</p>
                  <small>{t('admin.services.form.uploadHelp')}</small>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
            {uploadError && (
              <div className="text-danger small text-center">{uploadError}</div>
            )}
          </Tab>

        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          {t('common.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ImagePicker;
